import React from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Link from 'next/link'
import { withTranslation } from '~/i18n'
import getUrlFromLanguage from '~/utils/i18n/getUrlFromLanguage'
import {
  H3,
  H4,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  color,
  space,
  transition,
  device,
  Image,
  fontWeight,
  radius,
} from '@ticketswap/solar'
import { WidePageLayout } from '~/components/PageLayout'
import NativeMenuDropdown from './NativeMenuDropdown'
import { items } from './menu-items'
import img from './how-it-works.jpg'
import { useLanguage } from '../LanguageContext'
import { AppStoreLink, GooglePlayLink } from '../AppStoreBadges'
import { Apple, Android, ArrowDown } from '@ticketswap/comets'
import { useMutation } from '@apollo/client'
import CHANGE_USER_LOCALE from '~/graphql/mutations/changeUserLocale'
import { useViewer } from '~/components/ViewerContext'

const Container = styled.footer`
  background-color: ${color.nova};
  color: ${color.space};

  ${({ variant }) =>
    variant === 'default' &&
    css`
      padding-top: ${space[32]};
      border-top: 1px solid ${color.spaceLightest};
    `}
`

const List = styled.ul`
  display: grid;
  grid-gap: ${space[4]};
`

const HorizontalList = styled(List)`
  display: flex;
  flex-direction: row;
`

const IconWrapper = styled.span`
  display: inline-block;
  margin-right: ${space[8]};
  transform: translate3d(-2px, -2px, 0);
`

const navLinkStyles = css`
  color: ${color.spaceMedium};
  transition: color ${transition};
  display: flex;
  white-space: nowrap;
  align-items: center;

  &:hover,
  &:focus {
    color: ${color.space};
  }
`

const StyledAppStoreLink = styled(AppStoreLink)`
  ${navLinkStyles};
`

const StyledGooglePlayLink = styled(GooglePlayLink)`
  ${navLinkStyles};
`

const NavLink = styled.a`
  ${navLinkStyles};
`

const StyledH4 = styled(H4)`
  margin-bottom: ${space[16]};
`

const Appendix = styled.div`
  border-top: 1px solid ${color.spaceLightest};
  padding-top: ${space[32]};
  padding-bottom: ${space[32]};
`

const Grid = styled.div`
  padding-bottom: ${space[32]};

  @media ${device.tablet} {
    padding-top: ${space[24]};
    padding-bottom: ${space[64]};
    display: flex;
    flex-direction: row;
  }
`

const IslandAlpha = styled.div`
  margin-bottom: ${space[32]};

  @media ${device.tablet} {
    width: ${100 / 3}%;
    margin-bottom: 0;
    padding-right: ${space[8]};
  }
`

const IslandGamma = styled.div`
  @media ${device.tablet} {
    width: ${100 / 3}%;
    padding-left: ${space[8]};
  }
`

const IslandDelta = styled.div`
  @media ${device.tablet} {
    width: ${100 / 3}%;
    display: none;
  }
`

const NativeMenuDropdownWrapper = styled.div`
  @media ${device.tablet} {
    display: none;
  }
`

const PrimaryNavWrapper = styled.div`
  display: none;

  @media ${device.tablet} {
    display: block;
  }
`

const NavWrapper = styled.div`
  margin-bottom: ${space[32]};
`

const IslandBeta = styled.div`
  @media ${device.tablet} {
    width: ${100 / 3}%;
    padding-left: ${space[8]};
    padding-right: ${space[8]};
  }
`

const IslandBetaWrapper = styled(NavWrapper)`
  display: none;

  @media ${device.tablet} {
    display: block;
  }
`

const VideoWrapper = styled.div`
  margin-bottom: ${space[32]};

  @media ${device.tablet} {
    margin-bottom: 0;
  }
`

const MobileSocialNav = styled.nav`
  display: flex;
  justify-content: center;
`

const AppendixGrid = styled.div`
  display: flex;
`

const AppendixIslandAlpha = styled.div`
  display: none;

  @media ${device.tablet} {
    display: block;
    width: 50%;
  }
`

const LegalNavItem = styled.li`
  margin-right: ${space[24]};
`

const AppendixIslandBeta = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media ${device.tablet} {
    width: 50%;
  }
`

const LanguageMenuList = styled(MenuList)`
  position: relative;
  z-index: 1;
  margin-right: ${space[4]};
`

const LanguageMenuButton = styled(MenuButton)`
  span {
    color: ${color.spaceMedium};
  }
`

const LanguageArrowDown = styled(ArrowDown)`
  margin-left: ${space[8]};
`

const LanguageMenuItem = styled(MenuItem)`
  margin: 0 ${space[16]};
  border-radius: ${radius.sm};
`

const LanguageMenuText = styled(H3)`
  color: ${({ isCurrent }) => (isCurrent ? color.earthLight : color.spaceMedium)};
  font-weight: ${({ isCurrent }) => (isCurrent ? fontWeight.semiBold : fontWeight.regular)};
`

const SiteFooter = ({ t, variant }) => {
  const { viewer, deviceInfo } = useViewer()
  const { isDesktop, isMobile, isIOS, isApp, isAndroid } = deviceInfo
  const language = useLanguage()
  const [changeUserLocale] = useMutation(CHANGE_USER_LOCALE)
  const showAppStoreLink = isDesktop || ((isMobile || isApp) && isIOS)
  const showGooglePlayLink = isDesktop || ((isMobile || isApp) && isAndroid)
  const currentLocale =
    items.locales.find(locale => locale.value.split('-')[0] === language) || items.locales[0]

  return (
    <Container data-hide-feedback-widget="true" variant={variant}>
      {variant === 'default' && (
        <WidePageLayout>
          <Grid>
            <IslandAlpha>
              <StyledH4>{t('footer.primary.title')}</StyledH4>
              <nav aria-label="Primary navigation">
                <NativeMenuDropdownWrapper>
                  <NativeMenuDropdown t={t} />
                </NativeMenuDropdownWrapper>

                <PrimaryNavWrapper>
                  <List>
                    {items.primary.map(item => (
                      <li key={item.title}>
                        {item.url ? (
                          <NavLink href={item.url}>
                            {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                            {t(item.title)}
                          </NavLink>
                        ) : (
                          <Link href={item.href} as={item.as} passHref>
                            <NavLink>
                              {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                              {t(item.title)}
                            </NavLink>
                          </Link>
                        )}
                      </li>
                    ))}
                  </List>
                </PrimaryNavWrapper>
              </nav>
            </IslandAlpha>

            <IslandBeta>
              <IslandBetaWrapper>
                <StyledH4>{t('footer.social.title')}</StyledH4>
                <nav aria-label="Social channels">
                  <List>
                    {items.social.map(item => (
                      <li key={item.title}>
                        <NavLink href={item.url}>
                          {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                          {item.title}
                        </NavLink>
                      </li>
                    ))}
                  </List>
                </nav>
              </IslandBetaWrapper>

              <NavWrapper>
                <StyledH4>{t('footer.apps.title')}</StyledH4>
                <nav aria-label="Our apps">
                  <List>
                    {showAppStoreLink && (
                      <li>
                        <StyledAppStoreLink trackingContext="SITE_FOOTER">
                          <IconWrapper>
                            <Apple size={24} />
                          </IconWrapper>
                          {t('footer.apps.iosApp')}
                        </StyledAppStoreLink>
                      </li>
                    )}
                    {showGooglePlayLink && (
                      <li>
                        <StyledGooglePlayLink trackingContext="SITE_FOOTER">
                          <IconWrapper>
                            <Android size={24} />
                          </IconWrapper>
                          {t('footer.apps.androidApp')}
                        </StyledGooglePlayLink>
                      </li>
                    )}
                  </List>
                </nav>
              </NavWrapper>
            </IslandBeta>

            <IslandGamma>
              <NavWrapper>
                <StyledH4>{t('footer.help.title')}</StyledH4>
                <nav aria-label="Help navigation">
                  <List>
                    {items.help.map(({ href, as, icon, title }) => (
                      <li key={title}>
                        <Link as={as} href={href} passHref>
                          <NavLink>
                            {icon && <IconWrapper>{icon}</IconWrapper>}
                            {t(title)}
                          </NavLink>
                        </Link>
                      </li>
                    ))}
                  </List>
                </nav>
              </NavWrapper>

              <VideoWrapper>
                <StyledH4>{t('footer.howItWorks.title')}</StyledH4>
                <a
                  href="https://www.youtube.com/watch?v=6YISCKlqCS0"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image rounded src={img} width={1200} height={675} alt="How it works" />
                </a>
              </VideoWrapper>
            </IslandGamma>

            <IslandDelta>
              <MobileSocialNav>
                <HorizontalList>
                  {items.social.map(item => (
                    <li key={item.title}>
                      <NavLink href={item.href} aria-label={item.title}>
                        {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                      </NavLink>
                    </li>
                  ))}
                </HorizontalList>
              </MobileSocialNav>
            </IslandDelta>
          </Grid>
        </WidePageLayout>
      )}

      <Appendix>
        <WidePageLayout>
          <AppendixGrid>
            <AppendixIslandAlpha>
              <nav aria-label="Legal navigation">
                <HorizontalList>
                  {items.legal.map(item => (
                    <LegalNavItem key={item.title}>
                      <Link href={item.href} as={item.as} passHref>
                        <NavLink>{t(item.title)}</NavLink>
                      </Link>
                    </LegalNavItem>
                  ))}
                </HorizontalList>
              </nav>
            </AppendixIslandAlpha>
            <AppendixIslandBeta>
              <Menu>
                <LanguageMenuButton>
                  {t('footer.language.title')}:&nbsp; <span>{currentLocale.name}</span>
                  <span aria-hidden>
                    <LanguageArrowDown color={color.spaceMedium} size={12} />
                  </span>
                </LanguageMenuButton>
                <LanguageMenuList>
                  {items.locales.map(locale => (
                    <LanguageMenuItem
                      key={locale.value}
                      onSelect={async () => {
                        if (viewer) {
                          await changeUserLocale({
                            variables: {
                              input: {
                                locale: locale.value,
                              },
                            },
                          })
                        }

                        const href = `${getUrlFromLanguage(locale.value)}?language=${locale.value}`
                        window.location.assign(href)
                      }}
                    >
                      <LanguageMenuText isCurrent={currentLocale.value === locale.value}>
                        {locale.name}
                      </LanguageMenuText>
                    </LanguageMenuItem>
                  ))}
                </LanguageMenuList>
              </Menu>
            </AppendixIslandBeta>
          </AppendixGrid>
        </WidePageLayout>
      </Appendix>
    </Container>
  )
}

SiteFooter.defaultProps = {
  variant: 'default',
}

SiteFooter.propTypes = {
  t: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['default', 'appendix']),
}

export default withTranslation('common')(SiteFooter)
